import { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import type { IComment } from "../../../../../types/comment";
import apiService from "../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";

interface BulkConvertContactsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  comments: IComment[];
}

type ConvertStatus = "confirm" | "converting" | "done" | "error";

interface ConvertResult {
  created: number;
  skipped: number;
  errors: string[];
}

export const BulkConvertContactsModal: React.FC<
  BulkConvertContactsModalProps
> = ({ open, setOpen, comments }) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [status, setStatus] = useState<ConvertStatus>("confirm");
  const [result, setResult] = useState<ConvertResult | null>(null);

  const handleConvert = async () => {
    setStatus("converting");

    const payload = {
      comments: comments.map((c) => ({
        author: c.author,
        email: c.authorEmail === "Anonymous" ? null : c.authorEmail,
        phone: c.authorPhone || null,
        // Per row: a bulk selection here can span several memorial pages.
        stream_id: c.streamId ?? null,
      })),
    };

    try {
      const response = await apiService.post<ConvertResult>(
        "/api/contacts/contact/bulk-create-from-comments/",
        payload
      );
      setResult(response.data);
      setStatus("done");
      showSuccessToast(
        `${response.data.created} contact(s) created, ${response.data.skipped} skipped.`
      );
    } catch (err) {
      console.error("Bulk convert failed:", err);
      setStatus("error");
      showErrorToast("Failed to convert comments to contacts.");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          border: "2px solid #fff",
          boxShadow: 24,
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: ".8em 2em",
            borderBottom: `1px solid ${reduxTheme.primary_colors[300]}`,
          }}
        >
          <Typography variant="h6" component="h2">
            Convert Comments to Contacts
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ "&:hover": { color: "#a60b0bff" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ padding: "1.5em 2em" }}>
          {status === "confirm" && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You are about to convert{" "}
                <strong>{comments.length} comment(s)</strong> to individual
                contacts. Existing contacts with the same email will be skipped.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Comments without a last name (single-word authors) will be
                skipped.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleConvert}>
                  Convert
                </Button>
              </Box>
            </>
          )}

          {status === "converting" && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={48} />
              <Typography sx={{ mt: 2 }}>
                Converting {comments.length} comments to contacts...
              </Typography>
            </Box>
          )}

          {status === "done" && result && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CheckCircleIcon
                sx={{ fontSize: 48, color: "#4BA663", mb: 1 }}
              />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Conversion Complete
              </Typography>
              <Typography>
                <strong>{result.created}</strong> contact(s) created
              </Typography>
              <Typography>
                <strong>{result.skipped}</strong> skipped (duplicates or missing
                last name)
              </Typography>
              {result.errors.length > 0 && (
                <Box sx={{ mt: 2, textAlign: "left" }}>
                  <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                    Errors:
                  </Typography>
                  {result.errors.map((err, i) => (
                    <Typography key={i} variant="body2" color="error">
                      {err}
                    </Typography>
                  ))}
                </Box>
              )}
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
              >
                <Button variant="contained" onClick={handleClose}>
                  Close
                </Button>
              </Box>
            </Box>
          )}

          {status === "error" && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <ErrorIcon sx={{ fontSize: 48, color: "#d32f2f", mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Conversion Failed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Something went wrong. Please try again.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 2,
                }}
              >
                <Button variant="outlined" onClick={handleClose}>
                  Close
                </Button>
                <Button variant="contained" onClick={handleConvert}>
                  Retry
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
